import logger from '@eventbrite/client-logger';
import {
    getWindowObject,
    HAS_WINDOW,
    isAndroidWebView,
} from '@eventbrite/feature-detection';
import { writeText as writeTextToClipboard } from 'clipboard-polyfill/text';

export const getUserAgent = (): string =>
    getWindowObject('navigator')?.userAgent ||
    getWindowObject('navigator')?.vendor ||
    '';

export const isIosWebView = (): boolean =>
    /iphone|ipod|ipad/.test(getUserAgent().toLowerCase());

export const isFacebookWebView = (): boolean =>
    /fbav|fbios/.test(getUserAgent().toLowerCase());

export const isInstagramWebView = (): boolean =>
    getUserAgent().toLowerCase().includes('instagram');

export const isLinkedInWebView = (): boolean =>
    getUserAgent().toLowerCase().includes('linkedinapp');

export const isSocialWebView = () => {
    return isFacebookWebView() || isInstagramWebView() || isLinkedInWebView();
};

// https://developer.chrome.com/docs/multidevice/user-agent/
export const isChromeMobileWebView = () => {
    const ua = getUserAgent();
    return /Chrome\//.test(ua) && /wv\)/.test(ua) && /Mobile/.test(ua);
};

export const isSafari = (): boolean => {
    const ua = getUserAgent().toLowerCase();
    return (
        ua.includes('safari') &&
        !ua.includes('chrome') &&
        !ua.includes('crios') &&
        !ua.includes('fxios')
    );
};

export const isFirefox = (): boolean => {
    const ua = getUserAgent().toLowerCase();
    return ua.includes('firefox') || ua.includes('fxios');
};

const STAGE_ENV_URLS_REGEX = /^(https?:\/\/)?(www\.|dev\.)?evbstage\.com/;
const QA_ENV_URLS_REGEX = /^(https?:\/\/)?(www\.|dev\.)?evbqa(\d*)\.com/;
const DEV_ENV_URL_REGEX = /^(https?:\/\/)?(www\.|dev\.)?evbdev\.com/;

export const isSTAGEEnv = (host: string) => STAGE_ENV_URLS_REGEX.test(host);
export const isQAEnv = (host: string) => QA_ENV_URLS_REGEX.test(host);
export const isDevEnv = (host: string) => DEV_ENV_URL_REGEX.test(host);

/*
 * When having an asynchronous payment such as Sofort or Bancontact, the checkout flow
 * relies on having a second tab where the user performs the payment while the first one
 * stays idle on EB.
 * This two-tabs mechanics doesn't work for in-app browsers such as Facebook's, Instagram's
 * or LinkedIn on iOS as they simply do not support tabs. We therefore us an alternative flow
 * where EB's tab performs a redirection on demand by the user.
 * For more info on the implementation details, please refer to: EB-95856.
 *
 * This methods determine whether the first or the second flow should be followed.
 */
export const shouldPerformTablessCheckout = (): boolean => {
    const isOffendingAppWebView = isSocialWebView();
    const isWebView = isIosWebView() || isAndroidWebView();

    return isOffendingAppWebView && isWebView;
};

export enum RedirectionTechnique {
    'window.location = url' = 'window.location = url',
    'window.location.href = url' = 'window.location.href = url',
    'window.location.replace(url)' = 'window.location.replace(url)',
    'window.location.assign(url)' = 'window.location.assign(url)',
}

export const redirectTo = (url: string): Promise<RedirectionTechnique> =>
    new Promise((resolve, reject) => {
        /**
         * Workaround to redirect with multiple fallback support
         * https://stackoverflow.com/questions/31223216/why-isnt-window-location-href-not-forwarding-to-page-using-safari
         */
        const onExceptionRedirect = setTimeout(function () {
            const metadata = {
                redirectionTechnique:
                    RedirectionTechnique['window.location.assign(url)'],
                url,
            };

            // TODO: Rewrite this to work with the Sentry beforeSend signature
            // const notificationOverrides = {
            //     beforeSend: (report: Bugsnag.Report) => {
            //         // eslint-disable-next-line no-param-reassign
            //         report.groupingHash = 'tabless-environment-redirection';
            //     },
            // };

            const onTimeoutReject = setTimeout(function () {
                logger.error(
                    'Redirection fallback failed',
                    metadata,
                    // notificationOverrides,
                );

                reject(new Error(''));
            }, 100);

            logger.warn(
                'Redirection falled back to window.location.assign(url). This means there has been some unhanlded exception in the redirection process',
                metadata,
                // notificationOverrides,
            );

            window.location.assign(url);

            clearTimeout(onTimeoutReject);
            resolve(RedirectionTechnique['window.location.assign(url)']);
        }, 300);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if ((window.location = url)) {
            clearTimeout(onExceptionRedirect);

            return resolve(RedirectionTechnique['window.location = url']);
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if ((window.location.href = url)) {
            clearTimeout(onExceptionRedirect);

            return resolve(RedirectionTechnique['window.location.href = url']);
        }

        clearTimeout(onExceptionRedirect);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.location.replace(url);

        resolve(RedirectionTechnique['window.location.replace(url)']);
    });

export const getGoogleChromeUrlForCheckout = () => {
    const url = new URL(window.location.href);

    url.searchParams.delete('profile');

    return url.toString().replace(/^http?s:/, 'googlechrome:');
};

export const shareCheckoutUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('profile');
    url.searchParams.delete('modal');

    const urlString = url.toString();

    if (navigator.share) {
        return navigator.share({
            url: urlString,
        });
    }

    return writeTextToClipboard(urlString);
};

export const isEventbriteTLDInDevOrQAEnv = () => {
    if (!HAS_WINDOW) return false;

    const urlParams = new URLSearchParams(window.location.search);
    const parent = urlParams.get('parent') || '';
    return (
        (isQAEnv(window.location.host) && !parent) ||
        (isDevEnv(window.location.host) && !parent) ||
        (isQAEnv(window.location.host) && isQAEnv(parent)) ||
        (isDevEnv(window.location.host) && isDevEnv(parent))
    );
};

export const getScreenWidth = () =>
    getWindowObject('document')?.body.clientWidth;

export const browserSupportsGooglePay = () =>
    !isIosWebView() && !isSocialWebView() && !isSafari();

export const getNativeAppVersion = () => {
    // User agents with native app rendering web iframes looks like: 'Mozilla/5.0 (Linux; Android 14; SM-S928B Build/UP1A.231005.007; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/126.0.6478.71 Mobile Safari/537.36 Mobile Eventbrite/Attendee/B9.82.0-alpha'
    const pattern = /Eventbrite\/Attendee\/([^\s]+)/;
    const match = getUserAgent().match(pattern);
    return match?.[1] || '';
};
